import { useState, useEffect } from "react";
import { getSprintHistorico } from "../api/sprint.api";

const devColor = p => p >= 70 ? "#4ade80" : p >= 40 ? "#fbbf24" : "#f87171";

function normalizar(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map(item => {
    const saida = item.saida?.geral || item.indicadores?.saida?.geral || item.saida || item;
    const total = saida.total ?? saida.total_geral ?? 0;
    const pct   = saida.percentual ?? 0;
    let fechado = saida.fechado ?? saida.fechados ?? -1;
    if (fechado < 0) fechado = pct > 0 && total > 0 ? Math.round(pct * total / 100) : 0;
    const aberto = total - fechado;
    const pctFinal = pct > 0 ? pct : (total > 0 ? Math.round(fechado / total * 100) : 0);
    return {
      label: String(item.sprint || item.versao || item.grupo_entrega || "?"),
      total, fechado, aberto, pct: pctFinal,
    };
  })
  .filter(d => !isNaN(parseInt(d.label)))
  .sort((a, b) => parseInt(a.label) - parseInt(b.label));
}

export default function SprintComparar() {
  const [historico, setHistorico] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [sprintA,   setSprintA]   = useState("");
  const [sprintB,   setSprintB]   = useState("");

  useEffect(() => {
    getSprintHistorico()
      .then(data => { setHistorico(normalizar(data)); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (historico.length >= 2) {
      setSprintA(historico[historico.length - 2].label);
      setSprintB(historico[historico.length - 1].label);
    }
  }, [historico]);

  const dadoA = historico.find(h => h.label === sprintA);
  const dadoB = historico.find(h => h.label === sprintB);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh", color: "#475569" }}>
      Carregando sprints...
    </div>
  );

  if (historico.length < 2) return (
    <div style={page}>
      <h1 style={pageTitle}>Comparação de Sprints</h1>
      <div style={{ color: "#475569", marginTop: 40, textAlign: "center" }}>
        São necessárias pelo menos 2 sprints no histórico para comparar.
      </div>
    </div>
  );

  return (
    <div style={page}>

      {/* ── Header ── */}
      <div>
        <h1 style={pageTitle}>Comparação de Sprints</h1>
        <div style={pageSub}>Selecione duas sprints para ver a comparação detalhada</div>
      </div>

      {/* ── Seletores ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={selectLabel}>Sprint A</label>
          <select value={sprintA} onChange={e => setSprintA(e.target.value)} style={selectStyle}>
            {historico.map(h => <option key={h.label} value={h.label}>Sprint {h.label}</option>)}
          </select>
        </div>
        <div style={{ fontSize: 24, color: "#334155", alignSelf: "flex-end", marginBottom: 4 }}>vs</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={selectLabel}>Sprint B</label>
          <select value={sprintB} onChange={e => setSprintB(e.target.value)} style={selectStyle}>
            {historico.map(h => <option key={h.label} value={h.label}>Sprint {h.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── Comparativo ── */}
      {dadoA && dadoB && (
        <>
          {/* Cards lado a lado */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 16, alignItems: "start" }}>
            <SprintCard dados={dadoA} lado="A" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 24 }}>
              <DeltaChip label="Taxa de entrega" a={dadoA.pct}    b={dadoB.pct}    unit="%" />
              <DeltaChip label="Total"           a={dadoA.total}  b={dadoB.total}  unit="" />
              <DeltaChip label="Entregues"       a={dadoA.fechado} b={dadoB.fechado} unit="" />
              <DeltaChip label="Pendentes"       a={dadoA.aberto} b={dadoB.aberto} unit="" invertDelta />
            </div>
            <SprintCard dados={dadoB} lado="B" />
          </div>

          {/* Barra de comparação visual */}
          <div style={card}>
            <div style={cardTitle}>Comparativo visual</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { label: "Taxa de entrega", a: dadoA.pct,    b: dadoB.pct,    max: 100,                          unit: "%" },
                { label: "Total tickets",  a: dadoA.total,  b: dadoB.total,  max: Math.max(dadoA.total, dadoB.total, 1), unit: "" },
                { label: "Entregues",      a: dadoA.fechado, b: dadoB.fechado, max: Math.max(dadoA.total, dadoB.total, 1), unit: "" },
              ].map(({ label, a, b, max, unit }) => (
                <div key={label}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>{label}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: "#60a5fa", width: 70 }}>Sprint {dadoA.label}</span>
                      <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.04)", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(a / max) * 100}%`, background: "#60a5fa", borderRadius: 4, transition: "width 0.6s" }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#60a5fa", width: 40, textAlign: "right" }}>{a}{unit}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: "#a78bfa", width: 70 }}>Sprint {dadoB.label}</span>
                      <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.04)", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(b / max) * 100}%`, background: "#a78bfa", borderRadius: 4, transition: "width 0.6s" }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#a78bfa", width: 40, textAlign: "right" }}>{b}{unit}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Histórico completo como contexto */}
          <div style={card}>
            <div style={cardTitle}>Histórico completo — contexto das sprints selecionadas</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Sprint", "Total", "Entregues", "Pendentes", "Taxa de entrega"].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...historico].reverse().map((h, i) => {
                  const isSelected = h.label === sprintA || h.label === sprintB;
                  const cor = h.label === sprintA ? "#60a5fa" : h.label === sprintB ? "#a78bfa" : null;
                  return (
                    <tr key={i} style={{ background: isSelected ? `${cor}08` : i % 2 === 0 ? "rgba(96,165,250,0.02)" : "transparent", border: isSelected ? `1px solid ${cor}25` : "none" }}>
                      <td style={{ ...td, fontWeight: 700, color: cor ?? "#e2e8f0" }}>
                        {h.label}
                        {isSelected && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: cor, background: `${cor}15`, border: `1px solid ${cor}30`, borderRadius: 4, padding: "1px 5px" }}>
                          {h.label === sprintA ? "A" : "B"}
                        </span>}
                      </td>
                      <td style={{ ...td, textAlign: "center" }}>{h.total}</td>
                      <td style={{ ...td, textAlign: "center", color: "#4ade80", fontWeight: 700 }}>{h.fechado}</td>
                      <td style={{ ...td, textAlign: "center", color: "#fbbf24", fontWeight: 700 }}>{h.aberto}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 6, fontSize: 11, fontWeight: 800, color: devColor(h.pct), background: `${devColor(h.pct)}15`, border: `1px solid ${devColor(h.pct)}40` }}>
                          {h.pct}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Sub-componentes ─────────────────────────────────────────────── */

function SprintCard({ dados, lado }) {
  const c = devColor(dados.pct);
  const corLado = lado === "A" ? "#60a5fa" : "#a78bfa";
  return (
    <div style={{ ...card, borderColor: `${corLado}30`, background: `${corLado}06` }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: corLado, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>
        Sprint {dados.label} — {lado === "A" ? "Sprint A" : "Sprint B"}
      </div>
      <div style={{ fontSize: 44, fontWeight: 900, color: c, lineHeight: 1, marginBottom: 4 }}>{dados.pct}%</div>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 16 }}>taxa de entrega</div>
      <div style={{ height: 5, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ height: "100%", width: `${dados.pct}%`, background: c, borderRadius: 3, transition: "width 0.6s" }} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {[["Total", dados.total, "#94a3b8"], ["Entregues", dados.fechado, "#4ade80"], ["Pendentes", dados.aberto, "#fbbf24"]].map(([l, v, cc]) => (
          <div key={l} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: cc }}>{v}</div>
            <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeltaChip({ label, a, b, unit, invertDelta }) {
  const delta = b - a;
  const positive = invertDelta ? delta <= 0 : delta >= 0;
  const cor = delta === 0 ? "#475569" : positive ? "#4ade80" : "#f87171";
  return (
    <div style={{ textAlign: "center", padding: "8px 12px", borderRadius: 10, background: "rgba(11,22,42,0.6)", border: "1px solid rgba(96,165,250,0.08)", minWidth: 120 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 900, color: cor }}>
        {delta > 0 ? "+" : ""}{delta}{unit}
      </div>
      <div style={{ fontSize: 9, color: "#334155", marginTop: 2 }}>B vs A</div>
    </div>
  );
}

/* ─── Estilos ─────────────────────────────────────────────────────── */

const page      = { padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 };
const pageTitle = { color: "#f1f5f9", fontSize: 22, fontWeight: 700, margin: 0 };
const pageSub   = { color: "#475569", fontSize: 13, marginTop: 4 };
const card      = { background: "rgba(11,22,42,0.6)", border: "1px solid rgba(96,165,250,0.1)", borderRadius: 14, padding: "20px 22px" };
const cardTitle = { fontSize: 11, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 16 };
const th        = { padding: "8px 12px", fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, textAlign: "left", borderBottom: "1px solid rgba(96,165,250,0.1)" };
const td        = { padding: "10px 12px", fontSize: 12, color: "#94a3b8", borderBottom: "1px solid rgba(96,165,250,0.05)" };
const selectLabel = { fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.6 };
const selectStyle = {
  padding: "8px 14px", borderRadius: 9, fontSize: 13, fontWeight: 600,
  border: "1px solid rgba(96,165,250,0.2)", background: "rgba(11,22,42,0.8)",
  color: "#e2e8f0", outline: "none", cursor: "pointer", colorScheme: "dark",
  minWidth: 160,
};
